import { useCallback, useEffect, useState } from "react";
import { Lock, Plus, FileText, Image as ImageIcon, Eye, Download, Trash2, Loader2, ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

export const INTERNAL_DOC_TYPES = [
    "Orçamento subempreiteiro", "Orçamento fornecedor", "Fatura/recibo",
    "Ficha técnica", "Desenho/Projeto", "Outro",
];
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/gif";
const ALLOWED = ACCEPT.split(",");
const MAX_BYTES = 15 * 1024 * 1024;
const ACT = "inline-flex items-center gap-1.5 px-3.5 py-2.5 min-h-[40px] rounded-full border text-xs font-semibold transition-colors";
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

export default function LeadInternalDocs({ leadId }) {
    const [docs, setDocs] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/leads/${leadId}/internal-docs`);
            setDocs(data);
        } catch {
            setDocs([]);
            toast.error("Erro ao carregar documentos internos");
        }
    }, [leadId]);

    useEffect(() => { setDocs(null); load(); }, [load]);

    const remove = async (doc) => {
        if (!window.confirm("Eliminar este documento interno?")) return;
        try {
            await api.delete(`/admin/leads/${leadId}/internal-docs/${doc.id}`);
            setDocs((list) => list.filter((d) => d.id !== doc.id));
            toast.success("Documento eliminado.");
        } catch {
            toast.error("Erro ao eliminar documento");
        }
    };

    return (
        <div className="bg-white rounded-lg border border-stone-200 p-4 md:p-5" data-testid="lead-internal-docs">
            <div className="flex items-center gap-2 flex-wrap mb-3">
                <FileText className="w-4 h-4 text-stone-700" />
                <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">Documentos internos</span>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full" data-testid="internal-docs-lock">
                    <Lock className="w-3 h-3" /> Só visível para a equipa
                </span>
            </div>
            <button
                type="button"
                onClick={() => setAdding(true)}
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-800 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors"
                data-testid="internal-docs-add"
            >
                <Plus className="w-4 h-4" /> Adicionar documento
            </button>
            <InternalDocsList docs={docs} leadId={leadId} onDelete={remove} />
            {adding && (
                <AddInternalDocModal
                    leadId={leadId}
                    onClose={() => setAdding(false)}
                    onSaved={(doc) => { setDocs((list) => [doc, ...(list || [])]); setAdding(false); }}
                />
            )}
        </div>
    );
}

function InternalDocsList({ docs, leadId, onDelete }) {
    if (docs === null) return <div className="mt-4 text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>;
    if (!docs.length) return <p className="mt-3 text-xs text-stone-500" data-testid="internal-docs-empty">Ainda não há documentos internos nesta ficha.</p>;
    return (
        <ul className="mt-4 space-y-2.5" data-testid="internal-docs-list">
            {docs.map((d) => {
                const url = `${API}/admin/leads/${leadId}/internal-docs/${d.id}/file`;
                const Icon = (d.content_type || "").startsWith("image/") ? ImageIcon : FileText;
                return (
                    <li key={d.id} className="flex items-start gap-3 rounded-lg border border-stone-200 p-3" data-testid={`internal-doc-${d.id}`}>
                        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-stone-100 text-stone-700 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-stone-900 break-words" data-testid="internal-doc-name">{d.display_name}</div>
                            <div className="text-xs text-stone-600 mt-0.5">
                                <span data-testid="internal-doc-type">{d.document_type}</span>
                                <span className="text-stone-300 mx-1.5">·</span>
                                <span data-testid="internal-doc-date">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("pt-PT") : ""}</span>
                            </div>
                            {d.filename && <div className="text-[11px] text-stone-400 truncate mt-0.5" title={d.filename} data-testid="internal-doc-filename">{d.filename}</div>}
                            <div className="flex flex-wrap gap-2 mt-3">
                                <a href={url} target="_blank" rel="noreferrer" className={`${ACT} border-stone-300 text-stone-700 hover:border-[hsl(var(--copper))]`} data-testid="internal-doc-view"><Eye className="w-3.5 h-3.5" /> Ver</a>
                                <a href={`${url}?download=1`} download className={`${ACT} border-stone-300 text-stone-700 hover:border-[hsl(var(--copper))]`} data-testid="internal-doc-download"><Download className="w-3.5 h-3.5" /> Descarregar</a>
                                <button type="button" onClick={() => onDelete(d)} className={`${ACT} border-red-200 text-red-700 hover:bg-red-50`} data-testid="internal-doc-delete"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function AddInternalDocModal({ leadId, onClose, onSaved }) {
    const [file, setFile] = useState(null);
    const [name, setName] = useState("");
    const [type, setType] = useState("");
    const [saving, setSaving] = useState(false);

    const pick = (f) => {
        if (!f) return;
        if (!ALLOWED.includes(f.type)) return toast.error("Tipo de ficheiro não permitido (PDF ou imagem).");
        if (f.size > MAX_BYTES) return toast.error("Ficheiro demasiado grande (máx 15MB).");
        setFile(f);
    };

    const canSave = !!file && !!name.trim() && !!type && !saving;

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("display_name", name.trim());
            fd.append("document_type", type);
            const { data } = await api.post(`/admin/leads/${leadId}/internal-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento guardado.");
            onSaved(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao guardar documento");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-white md:bg-stone-950/60 md:flex md:items-center md:justify-center md:p-4" data-testid="internal-doc-modal">
            <div className="bg-white w-full h-full md:h-auto md:max-w-lg md:max-h-[90vh] md:rounded-xl md:shadow-2xl flex flex-col overflow-hidden">
                <div className="sticky top-0 z-10 bg-white border-b border-stone-100 px-4 py-3 flex items-center gap-3">
                    <button onClick={onClose} className="p-2 -ml-1 rounded-full hover:bg-stone-100 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="internal-doc-cancel" aria-label="Voltar">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base md:text-lg">Adicionar documento interno</div>
                        <div className="text-xs text-stone-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Só visível para a equipa</div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5 pb-28 md:pb-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Ficheiro *</div>
                        {file ? (
                            <div className="flex items-center gap-3 rounded-lg border border-stone-200 p-3" data-testid="internal-doc-selected">
                                <FileText className="w-5 h-5 text-stone-500 flex-shrink-0" />
                                <span className="flex-1 min-w-0 text-sm text-stone-800 truncate" title={file.name}>{file.name}</span>
                                <button type="button" onClick={() => setFile(null)} className="p-2 rounded-full hover:bg-stone-100" aria-label="Remover ficheiro" data-testid="internal-doc-remove-file"><X className="w-4 h-4" /></button>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-stone-300 rounded-xl p-6 cursor-pointer hover:border-[hsl(var(--copper))] transition-colors">
                                <Upload className="w-6 h-6 text-stone-400" />
                                <span className="text-sm text-stone-700 font-medium">Escolher ficheiro</span>
                                <span className="text-xs text-stone-500">PDF ou imagem · máx 15MB</span>
                                <input type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="internal-doc-file-input" />
                            </label>
                        )}
                    </div>

                    <label className="block">
                        <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold block mb-2">Nome / descrição *</span>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={200}
                            placeholder="Ex.: Orçamento eletricista — João Silva"
                            className="w-full px-3 py-3 border border-stone-300 rounded-lg text-base md:text-sm"
                            data-testid="internal-doc-name-input"
                        />
                    </label>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Tipo *</div>
                        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tipo de documento">
                            {INTERNAL_DOC_TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    role="radio"
                                    aria-checked={type === t}
                                    onClick={() => setType(t)}
                                    className={`min-h-[48px] px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${type === t ? "border-[hsl(var(--copper))] bg-[hsl(var(--copper))]/10 text-stone-900 font-semibold" : "border-stone-200 text-stone-700 hover:border-stone-300"}`}
                                    data-testid={`internal-doc-type-${slug(t)}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="fixed md:static bottom-0 inset-x-0 bg-white border-t border-stone-100 p-4">
                    <button
                        type="button"
                        onClick={save}
                        disabled={!canSave}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-[hsl(var(--forest))] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="internal-doc-save"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
}
